import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useWhoAmIQuery } from "@generated";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import useAccountHasEntitlement from "@common/hooks/useAccountHasEntitlement";
import useFlags from "@common/hooks/useFlags";
import settingsNavItems, {
  resolveActiveNav,
  SettingsNavGate,
} from "./settingsNavItems";

export const SETTINGS_NAV_HEIGHT = 44;
export const SETTINGS_TITLE_HEIGHT = 75;
export const SETTINGS_HEADER_HEIGHT =
  SETTINGS_TITLE_HEIGHT + SETTINGS_NAV_HEIGHT;

export const settingsTitleStyle: React.CSSProperties = {
  height: SETTINGS_TITLE_HEIGHT,
  display: "flex",
  alignItems: "center",
  margin: 0,
  padding: "0 16px",
  fontFamily: "Open Sans, sans-serif",
  fontSize: 36,
  fontWeight: 700,
  color: "#1C2655",
};

export interface SettingsNavigationProviderProps {
  sidebarWidth: number;
  /** When true (inside SettingsAppLayout's content router), react children
   *  navigate client-side. When false/omitted (chrome-only SettingsLayout),
   *  all children fall back to full-page GETs. */
  clientSideNav?: boolean;
}

const tierOneStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: 0,
  fontFamily: "Open Sans, sans-serif",
  fontSize: 14,
  fontWeight: 700,
  position: "relative",
  height: SETTINGS_NAV_HEIGHT,
};

const tierOneItemStyle = (isActive: boolean): React.CSSProperties => ({
  padding: "0 16px",
  height: "100%",
  display: "flex",
  alignItems: "center",
  color: "#1C2655",
  backgroundColor: isActive ? "#F8F9FA" : "transparent",
  borderRadius: isActive ? "4px 4px 0 0" : 0,
  cursor: "default",
  whiteSpace: "nowrap",
  textDecoration: "none",
  transition: "background-color 0.15s ease",
  position: "relative",
});

const tierTwoStyle: React.CSSProperties = {
  position: "absolute",
  top: "100%",
  left: 0,
  display: "flex",
  flexDirection: "column",
  minWidth: 200,
  backgroundColor: "#F8F9FA",
  border: "1px solid #D7D7D7",
  borderTop: "none",
  borderRadius: "0 0 4px 4px",
  fontFamily: "Open Sans, sans-serif",
  fontSize: 13,
  fontWeight: 400,
  zIndex: 9,
  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  padding: "4px 0",
};

const tierTwoItemStyle = (isActive: boolean): React.CSSProperties => ({
  padding: "8px 16px",
  color: "#1C2655",
  whiteSpace: "nowrap",
  textDecoration: "none",
  display: "block",
  backgroundColor: isActive ? "rgba(0,0,0,0.04)" : "transparent",
  transition: "background-color 0.15s ease",
});

const SettingsNavigationProvider: React.FC<SettingsNavigationProviderProps> = ({
  sidebarWidth,
  clientSideNav = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: me } = useWhoAmIQuery();
  const isSiteAdmin = me?.data?.attributes?.["site-admin"] === true;
  const accountSlug = me?.data?.attributes?.["account-slug"] ?? "";

  const { hasPrivilege } = usePrivilegeCheck();
  const { hasEntitlement } = useAccountHasEntitlement();
  const flags = useFlags() as Record<string, boolean>;

  const gatePasses = (gate?: SettingsNavGate): boolean => {
    if (!gate) return true;
    if (
      gate.privilege &&
      !hasPrivilege(
        gate.privilege,
        gate.accessLevels ?? ["view", "create", "manage"]
      )
    ) {
      return false;
    }
    if (gate.entitlement && !hasEntitlement(gate.entitlement)) return false;
    if (gate.flag && !flags[gate.flag]) return false;
    return true;
  };

  const active = resolveActiveNav(location.pathname);

  const visibleSections = settingsNavItems
    .map((section) => ({
      ...section,
      children: section.children.filter((child) => gatePasses(child.gate)),
    }))
    .filter(
      (section) =>
        (!section.siteAdminOnly || isSiteAdmin) && section.children.length > 0
    );

  const handleSectionEnter = (sectionId: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setActiveSection(sectionId);
  };

  const handleNavLeave = () => {
    timeoutRef.current = setTimeout(() => setActiveSection(null), 150);
  };

  const handleTierTwoEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        left: sidebarWidth,
        zIndex: 100,
        backgroundColor: "#fff",
        borderBottom: "1px solid #D7D7D7",
        transition: "left 0.2s ease",
      }}
    >
      <h1 style={settingsTitleStyle}>Settings</h1>
      <div style={tierOneStyle} onMouseLeave={handleNavLeave}>
        {visibleSections.map((section) => {
          const isHoverActive = activeSection === section.id;
          const isPathActive = active?.sectionId === section.id;
          return (
            <div
              key={section.id}
              id={`settings-nav-${section.id}`}
              data-active={isPathActive ? "true" : "false"}
              style={tierOneItemStyle(isHoverActive || isPathActive)}
              onMouseEnter={() => handleSectionEnter(section.id)}
            >
              {section.label}

              {isHoverActive && section.children.length > 0 && (
                <div
                  style={tierTwoStyle}
                  onMouseEnter={handleTierTwoEnter}
                  onMouseLeave={handleNavLeave}
                >
                  {section.children.map((child) => {
                    const childActive = active?.childId === child.id;
                    return (
                      <a
                        key={child.id}
                        id={child.id}
                        href={child.path.replace(":slug", accountSlug)}
                        style={tierTwoItemStyle(childActive)}
                        onClick={(e) => {
                          if (clientSideNav && child.routing === "react") {
                            e.preventDefault();
                            navigate(child.path.replace(":slug", accountSlug));
                            setActiveSection(null);
                          }
                          // rails child, or chrome-only usage: default anchor => full-page GET
                        }}
                        onMouseEnter={(e) => {
                          (
                            e.currentTarget as HTMLElement
                          ).style.backgroundColor = "rgba(0,0,0,0.04)";
                        }}
                        onMouseLeave={(e) => {
                          (
                            e.currentTarget as HTMLElement
                          ).style.backgroundColor = childActive
                            ? "rgba(0,0,0,0.04)"
                            : "transparent";
                        }}
                      >
                        {child.label}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SettingsNavigationProvider;
